(() => {
  'use strict';
  // Shopify's live list filter omitted entries after position 100. Read the
  // complete Liquid list and use ONE predicate for counts and product pages.
  const KEY = 'filter.p.m.custom.vehicle_submodel';
  const MAKE_KEY = 'filter.p.m.custom.vehicle_make';
  const MODEL_KEY = 'filter.p.m.custom.vehicle_model';
  const YEAR_KEY = 'filter.p.m.custom.by_year';
  const CACHE_TTL = 5 * 60 * 1000;
  const cache = new Map();
  const displayableSubmodel = value => typeof value === 'string' && value.trim();
  const EXTRA_VALUES = new Set(['Merchandise', 'Payment Adjustment']);
  const list = value => Array.isArray(value) ? value : value ? [value] : [];
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  function baseURL(input) {
    const u = new URL(input, location.href);
    [KEY, 'page', 'section_id', 'view', '_nc_refresh', '_nc_s', '_nc_u', '_nc_collection_root'].forEach(k => u.searchParams.delete(k));
    u.searchParams.sort();
    return u;
  }
  async function request(url) {
    const u = new URL(url); u.searchParams.set('_nc_refresh', String(Date.now()));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const r = await fetch(u, {credentials: 'same-origin', cache: 'no-store', signal: controller.signal});
      if (!r.ok) throw new Error('Collection request failed');
      return await r.text();
    } finally { clearTimeout(timer); }
  }
  async function index(input) {
    const base = baseURL(input), key = base.href;
    let item = cache.get(key);
    // Keep one complete Collection index warm for the browsing session. A 30s
    // TTL caused broad Collections to restart the same multi-page audit while the
    // customer was simply paging through products.
    if (item && Date.now() - item.time < CACHE_TTL) return item.promise;
    item = {time: Date.now(), promise: (async () => {
      const products = [], seen = new Set(); let page = 1, total;
      do {
        const u = new URL(base); u.searchParams.set('view', 'nicecnc-audit'); u.searchParams.set('page', page);
        const d = JSON.parse(await request(u));
        if (!Array.isArray(d.products) || !Number.isInteger(d.total) || d.page !== page) throw new Error('Invalid collection index');
        if (total !== undefined && total !== d.total) throw new Error('Collection changed during loading');
        total = d.total;
        for (const p of d.products) {
          if (!p.handle || seen.has(p.handle)) throw new Error('Inconsistent collection pagination');
          seen.add(p.handle); products.push(p);
        }
        if (d.next !== null && d.next !== page + 1) throw new Error('Invalid next page');
        page = d.next;
      } while (page);
      if (products.length !== total) throw new Error('Incomplete collection index');
      return products;
    })()};
    cache.set(key, item);
    if (cache.size > 8) cache.delete(cache.keys().next().value);
    item.promise.catch(() => { if (cache.get(key) === item) cache.delete(key); });
    return item.promise;
  }
  function matches(product, input, operator = 'OR') {
    const selected = new URL(input, location.href).searchParams.getAll(KEY).filter(displayableSubmodel);
    const values = list(product.submodel);
    return !selected.length || (operator === 'AND' ? selected.every(v => values.includes(v)) : selected.some(v => values.includes(v)));
  }
  function operator(scope) {
    return scope?.querySelector(`[data-nicecnc-filter-param="${KEY}"]`)?.dataset.nicecncFilterOperator || 'OR';
  }
  function needsFullRender(scope) {
    const root = scope?.matches?.('facet-filters') ? scope : scope?.querySelector?.('facet-filters');
    const group = root?.querySelector?.(`[data-nicecnc-filter-param="${KEY}"]`);
    if (!group) return false;
    if (group.dataset.nicecncMissingFacet === '1') return true;
    // Shopify list facets cap visible values at 100. Below that threshold the
    // native facet is complete, so use Shopify's fast AJAX path and avoid the
    // paginated collection audit entirely. Exactly 100 is treated as truncated.
    return group.querySelectorAll(`input[name="${KEY}"]`).length >= 100;
  }
  function visibleGroup(group) {
    return !!group && !group.hidden && group.style.display !== 'none' && group.dataset.nicecncCascadeGroupHidden !== '1';
  }
  function concreteSelected(url, key) {
    return url.searchParams.getAll(key).filter(v => v && v !== 'Universal');
  }
  function broadMakeFirstRoot(scope) {
    const root = scope?.matches?.('facet-filters') ? scope : scope?.querySelector?.('facet-filters');
    if (root?.dataset?.nicecncMakeFirst === '1') return true;
    const group = root?.querySelector?.(`[data-nicecnc-filter-param="${MAKE_KEY}"]`);
    if (!visibleGroup(group)) return false;
    const concrete = [...group.querySelectorAll(`input[name="${MAKE_KEY}"]`)].filter(i => i.value && i.value !== 'Universal');
    return concrete.length > 1;
  }
  // Blocking custom rendering is only needed once a Submodel filter is actually
  // part of the requested result. Make and Model clicks can always use Shopify's
  // native AJAX immediately; the complete Submodel option list is restored later
  // in the background when that layer is reached.
  function shouldUseBlockingRender(scope, input = location.href) {
    const url = new URL(input, location.href);
    if (!needsFullRender(scope)) return false;
    return concreteSelected(url, KEY).length > 0 || url.searchParams.has('_nc_s');
  }
  // Background full-list work is also gated by the visible hierarchy. On a broad
  // Make-first page do nothing while the customer is still choosing Make/Model.
  // If Model is skipped because only one live Model exists, Submodel becomes
  // visible and the audit is allowed to start asynchronously at that point.
  function deferBackgroundAudit(scope, input = location.href) {
    const url = new URL(input, location.href);
    if (concreteSelected(url, KEY).length || url.searchParams.has('_nc_s')) return false;
    if (!broadMakeFirstRoot(scope)) return false;
    const root = scope?.matches?.('facet-filters') ? scope : scope?.querySelector?.('facet-filters');
    const makeSelected = concreteSelected(url, MAKE_KEY).length > 0;
    const modelSelected = concreteSelected(url, MODEL_KEY).length > 0;
    if (!makeSelected) return true;
    if (modelSelected) return false;
    const modelGroup = root?.querySelector?.(`[data-nicecnc-filter-param="${MODEL_KEY}"]`);
    return visibleGroup(modelGroup);
  }
  function fillOptions(scope, products, url) {
    const group = scope.querySelector(`[data-nicecnc-filter-param="${KEY}"]`);
    if (!group) throw new Error('Missing Submodel group');
    group.removeAttribute('data-nicecnc-missing-facet');
    if (!group.dataset.nicecncFilterOperator) group.dataset.nicecncFilterOperator = 'OR';
    const container = group.querySelector('.collapsible__content');
    if (!container) throw new Error('Missing Submodel options');
    const values = [...new Set(products.flatMap(p => list(p.submodel)).filter(displayableSubmodel))];
    const selected = new Set(url.searchParams.getAll(KEY).filter(displayableSubmodel));
    selected.forEach(v => {if (!values.includes(v)) values.push(v);});
    if (!values.includes('Universal')) values.push('Universal');
    container.replaceChildren();
    values.forEach((value, i) => {
      const row = document.createElement('div'); row.className = 'checkbox-container';
      const input = document.createElement('input'); input.type = 'checkbox'; input.className = 'checkbox';
      input.id = 'nc-full-sub-' + i; input.name = KEY; input.value = value;
      input.checked = selected.has(value);
      if (input.checked) input.setAttribute('checked', 'checked');
      const label = document.createElement('label'); label.htmlFor = input.id;
      label.append(document.createTextNode(value + ' ('));
      const count = document.createElement('span'); count.setAttribute('data-nicecnc-submodel-count', '');
      // A concrete Submodel click always includes Universal products as an OR bridge.
      // Its displayed baseline count must therefore match what the customer will actually see:
      // concrete Submodel UNION Universal. The Universal row itself still shows Universal only.
      const baselineCount = products.filter(p => {
        const submodels = list(p.submodel);
        // Universal and non-vehicle buckets keep their own native counts.
        // Only a real vehicle Submodel inherits Universal products.
        if (value === 'Universal' || EXTRA_VALUES.has(value)) return submodels.includes(value);
        return submodels.includes(value) || submodels.includes('Universal');
      }).length;
      count.dataset.nativeCount = String(baselineCount);
      count.dataset.nicecncCountSource = 'baseline-submodel';
      count.textContent = String(baselineCount); label.append(count, document.createTextNode(')'));
      row.append(input, label); container.append(row);
    });
  }
  async function render(input, facet) {
    const url = new URL(input, location.href), base = baseURL(url);
    const sid = facet.getAttribute('section-id');
    const sectionURL = page => {const u = new URL(base); u.searchParams.set('section_id', sid); u.searchParams.set('page', page); return u;};
    const [products, html] = await Promise.all([index(url), request(sectionURL(1))]);
    const doc = parse(html), main = doc.querySelector('#facet-main');
    if (!main) throw new Error('Missing collection results');
    const size = Number(main.dataset.nicecncPageSize);
    if (!Number.isInteger(size) || size < 1) throw new Error('Missing page size');
    if (Number(main.dataset.nicecncResultCount) !== products.length) throw new Error('Collection changed during loading');
    const matched = products.map((p, rank) => ({p, rank})).filter(({p}) => matches(p, url, operator(doc)));
    const pages = Math.max(1, Math.ceil(matched.length / size));
    const page = Math.min(pages, Math.max(1, Number(url.searchParams.get('page')) || 1));
    const wanted = matched.slice((page - 1) * size, page * size);
    const sourcePages = [...new Set(wanted.map(({rank}) => Math.floor(rank / size) + 1))];
    const docs = new Map([[1, doc]]);
    // Bound concurrent requests. Cards are rendered by the original section,
    // preserving its settings, prices, variants, quick-buy and product markup.
    const pending = sourcePages.filter(n => n !== 1);
    await Promise.all(Array.from({length: Math.min(3, pending.length)}, async () => {
      while (pending.length) {const n = pending.shift(); docs.set(n, parse(await request(sectionURL(n))));}
    }));
    const cards = new Map();
    for (const d of docs.values()) for (const card of d.querySelectorAll('#facet-main .product-list__inner > *')) {
      const link = card.querySelector('a[href*="/products/"]');
      if (link) {const path = new URL(link.getAttribute('href'), location.origin).pathname; cards.set(decodeURIComponent(path.split('/products/')[1]?.split('/')[0]), card);}
    }
    const inner = main.querySelector('.product-list__inner');
    if (wanted.length && !inner) throw new Error('Missing product grid');
    if (inner) {
      inner.replaceChildren(...wanted.map(({p}) => {
        const card = cards.get(p.handle);
        if (!card) throw new Error('Product card missing: ' + p.handle);
        return card.cloneNode(true);
      }));
    }
    main.dataset.nicecncResultCount = String(matched.length);
    const count = main.querySelector('.product-facet__meta-bar-item--count');
    if (count) count.textContent = `${matched.length} ${matched.length === 1 ? 'product' : 'products'}`;
    main.querySelectorAll('page-pagination').forEach(e => e.remove());
    if (matched.length === 0 && inner) {
      const empty = document.createElement('p'); empty.className = 'empty-state'; empty.textContent = 'No matching products.'; inner.append(empty);
    }
    if (pages > 1) {
      const pager = document.createElement('page-pagination'); pager.setAttribute('ajax', ''); pager.className = 'pagination';
      const nav = document.createElement('nav'); nav.className = 'pagination__nav';
      const numbers = [...new Set([1, page - 1, page, page + 1, pages])].filter(n => n >= 1 && n <= pages).sort((a,b) => a-b);
      numbers.forEach((n, i) => {
        if (i && n - numbers[i-1] > 1) nav.append(document.createTextNode(' … '));
        const a = document.createElement(n === page ? 'span' : 'a'); a.className = 'pagination__nav-item heading heading--small'; a.textContent = String(n);
        if (n === page) a.setAttribute('aria-current', 'page');
        else {const u = new URL(url); u.searchParams.delete('section_id'); u.searchParams.delete('_nc_refresh'); u.searchParams.set('page', n); a.href = u.href; a.dataset.page = n;}
        nav.append(a);
      });
      pager.append(nav); main.append(pager);
    }
    fillOptions(doc, products, url);
    return doc.documentElement.outerHTML;
  }
  window.NICECNCFullSubmodels = {index, matches, render, fillOptions, needsFullRender, shouldUseBlockingRender, count: async (url, root) => (await index(url)).filter(p => matches(p, url, operator(root))).length};
  // Do not pre-warm the expensive full Collection index before a customer has
  // chosen Make on a broad multi-brand page. This is the main protection for All
  // Products: pagination and ordinary browsing stay on Shopify's native fast path.

  // Focal pagination now stays on its native fast AJAX path. That native section
  // response can contain only Shopify's first 100 Submodel facet values, so put
  // the complete option list back *after* the product page has rendered. This is
  // deliberately non-blocking: product navigation never waits for the audit.
  let restoreToken = 0;
  document.addEventListener('nicecnc:facets-rendered', event => {
    const facet = event.target?.matches?.('product-facet') ? event.target : event.target?.closest?.('product-facet');
    if (!facet || !location.pathname.includes('/collections/')) return;
    const token = ++restoreToken, snapshot = location.href;
    // Cascade applies visibility on the next frame. Check one frame later so a
    // single-brand Collection with a hidden Make facet is never mistaken for a
    // broad Make-first page.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (token !== restoreToken || location.href !== snapshot || facet.dataset.nicecncLoading) return;
      if (!needsFullRender(facet) || deferBackgroundAudit(facet, snapshot)) return;
      index(snapshot).then(products => {
        if (token !== restoreToken || location.href !== snapshot || facet.dataset.nicecncLoading) return;
        fillOptions(facet, products, new URL(snapshot));
        facet.dispatchEvent(new CustomEvent('nicecnc:full-submodels-restored', {bubbles:true}));
      }).catch(() => {});
    }));
  });
  let initStarted = false;
  function init() {
    if (initStarted) return;
    const facet = document.querySelector('product-facet');
    if (!facet || !location.pathname.includes('/collections/')) return;
    initStarted = true;
    // Native Shopify facets are complete below the 100-value cap. On broad
    // multi-brand Collections, also wait for an explicit Make choice before any
    // full-list audit starts.
    if (!needsFullRender(facet) || deferBackgroundAudit(facet, location.href)) return;
    // Direct filtered links and reloads must take the same path as clicks.
    if (new URL(location.href).searchParams.has(KEY)) {
      facet.dispatchEvent(new CustomEvent('facet:criteria-changed', {bubbles:true, detail:{url:location.href}}));
    } else {
      const snapshot = location.href;
      index(snapshot).then(products => {
        if (location.href !== snapshot || facet.dataset.nicecncLoading) return;
        fillOptions(facet, products, new URL(snapshot));
        facet.dispatchEvent(new CustomEvent('nicecnc:facets-rendered', {bubbles:true}));
      }).catch(() => {});
    }
  }
  // Let the cascade finish its first visibility pass before deciding whether the
  // lazy Make gate is active. Two animation frames are still imperceptible but
  // prevent false broad-page detection from raw Shopify facet markup.
  const scheduleInit = () => requestAnimationFrame(() => requestAnimationFrame(init));
  scheduleInit();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleInit, {once:true});
})();
